import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItem,
  ListItemText,
  Typography
} from "@material-ui/core";
import { format, parseISO } from "date-fns";

import toastError from "../../errors/toastError";
import { fetchKanbanHistory } from "../../services/kanban";

const sourceLabels = {
  manual: "Movimento manual",
  legacy: "Movimento pelo fluxo anterior",
  automation: "Automação",
  reply: "Retorno do cliente"
};

const deliveryLabels = {
  none: "Sem mensagem automática",
  pending: "Mensagem aguardando envio",
  processing: "Mensagem em processamento",
  sent: "Mensagem enviada",
  cancelled: "Envio cancelado",
  unknown: "Envio não confirmado; confira o atendimento"
};

const displayStage = name => name || "Sem etapa";

const KanbanHistoryDialog = ({ open, onClose, ticket }) => {
  const ticketId = ticket?.id;
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [hasMore, setHasMore] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const requestRef = useRef(0);

  const loadHistory = useCallback(async (beforeId = null) => {
    if (!ticketId) return;

    const requestId = ++requestRef.current;
    beforeId ? setLoadingMore(true) : setLoading(true);
    setError("");

    try {
      const { data } = await fetchKanbanHistory(ticketId, beforeId);
      if (requestId !== requestRef.current) return;

      setEvents(current => beforeId ? [...current, ...(data.events || [])] : (data.events || []));
      setHasMore(Boolean(data.hasMore));
      setNextCursor(data.nextCursor || null);
    } catch (err) {
      if (requestId !== requestRef.current) return;
      setError("Não foi possível carregar o histórico.");
      toastError(err);
    } finally {
      if (requestId === requestRef.current) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  }, [ticketId]);

  useEffect(() => {
    if (!open || !ticketId) return undefined;

    setEvents([]);
    setHasMore(false);
    setNextCursor(null);
    loadHistory();

    return () => {
      requestRef.current += 1;
    };
  }, [open, ticketId, loadHistory]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" aria-labelledby="kanban-history-title">
      <DialogTitle id="kanban-history-title">
        Histórico do atendimento #{ticketId}
      </DialogTitle>
      <DialogContent dividers>
        {loading && (
          <div style={{ display: "flex", justifyContent: "center", padding: 24 }}>
            <CircularProgress size={28} />
          </div>
        )}

        {!loading && error && (
          <Typography color="error" align="center">{error}</Typography>
        )}

        {!loading && !error && events.length === 0 && (
          <Typography color="textSecondary" align="center">
            Nenhum movimento registrado para este atendimento.
          </Typography>
        )}

        {!loading && events.length > 0 && (
          <List disablePadding>
            {events.map((event, index) => {
              const deliveryStatus = deliveryLabels[event.deliveryStatus] || deliveryLabels.unknown;
              const source = sourceLabels[event.source] || "Origem não identificada";
              const createdAt = event.createdAt
                ? format(parseISO(event.createdAt), "dd/MM/yyyy HH:mm")
                : "Data não informada";

              return (
                <React.Fragment key={event.id}>
                  {index > 0 && <Divider component="li" />}
                  <ListItem alignItems="flex-start">
                    <ListItemText
                      primary={`${displayStage(event.fromName)} → ${displayStage(event.toName)}`}
                      secondary={(
                        <>
                          <Typography component="span" variant="body2" color="textPrimary">
                            {source} · {event.userName || "Sistema"}
                          </Typography>
                          <br />
                          {createdAt} · {deliveryStatus}
                        </>
                      )}
                    />
                  </ListItem>
                </React.Fragment>
              );
            })}
          </List>
        )}

        {!loading && hasMore && (
          <div style={{ display: "flex", justifyContent: "center", paddingTop: 12 }}>
            <Button
              variant="outlined"
              color="primary"
              disabled={loadingMore || !nextCursor}
              onClick={() => loadHistory(nextCursor)}
            >
              {loadingMore ? <CircularProgress size={20} /> : "Carregar mais"}
            </Button>
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">Fechar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default KanbanHistoryDialog;
